import type { Column } from "@v1/components/designSystem/table/Table";
import type { TableMetric } from "@v2/features/analytics/constants";
import type { RangeResult } from "schemas/generated/analyticsRead";

import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import Table from "@v1/components/designSystem/table/Table";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import { TablePageCellContent } from "@v1/features/analytics/TablePageCellContent";
import { formatValueToTableCell } from "@v2/features/analytics/utils/formatMetric";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";

const TOP_PAGES_TABLE_METRICS: {
  key: TableMetric;
  label: string;
  tooltipContent: string;
}[] = [
  {
    key: "unique_sessions",
    label: "Unique Sessions",
    tooltipContent: "Unique Sessions Definition",
  },
  {
    key: "conversion_rates",
    label: "Conversion Rate",
    tooltipContent: "Conversion Rate Definition",
  },
];

const PAGE_NAME_COLUMN_WIDTH = "280px";

type TopPagesTableRow = {
  key: string;
  urlPath: string;
  metadata: RangeResult["metadata"];
  metrics: RangeResult["metrics"];
};

type TopPagesTableProps = {
  isLoading: boolean;
  topPages: RangeResult[];
};

export const TopPagesTable = ({ isLoading, topPages }: TopPagesTableProps) => {
  const analytics = useLogAnalytics();
  const { dispatchAnalyticsQuery } = useAnalyticsQueryContext();

  const handleUrlPathClick = React.useCallback(() => {
    analytics("analytics.url.open", {
      source: "analytics_overview",
    });
  }, [analytics]);

  const handlePageTitleClick = React.useCallback(
    (urlPath: string) => {
      dispatchAnalyticsQuery({
        type: "openDeepDivePageFromOverviewPage",
        payload: {
          urlPath,
          source: "analytics_overview",
        },
      });
    },
    [dispatchAnalyticsQuery],
  );

  const tableData: TopPagesTableRow[] = React.useMemo(() => {
    return topPages.map((result) => ({
      key: result.metadata.url,
      urlPath: result.urlPath,
      metadata: result.metadata,
      metrics: result.metrics,
    }));
  }, [topPages]);

  const columns: Column<TopPagesTableRow>[] = React.useMemo(() => {
    const cols: Column<TopPagesTableRow>[] = [
      {
        key: "page",
        header: "Page Name",
        width: PAGE_NAME_COLUMN_WIDTH,
        renderCell: (row) => (
          <TablePageCellContent
            title={row.metadata.title}
            url={row.metadata.url}
            urlPath={row.urlPath}
            onPageTitleClick={() => handlePageTitleClick(row.urlPath)}
            onUrlPathClick={handleUrlPathClick}
          />
        ),
      },
    ];

    TOP_PAGES_TABLE_METRICS.forEach((metric) => {
      cols.push({
        key: metric.key,
        header: (
          <Tooltip triggerAsChild content={metric.tooltipContent}>
            <span>{metric.label}</span>
          </Tooltip>
        ),
        renderCell: (row) => {
          const value = row.metrics[metric.key]?.[0];
          return (
            <span className="text-xs">
              {value
                ? formatValueToTableCell({
                    value,
                    name: metric.key,
                    currency: "USD",
                  })
                : "-"}
            </span>
          );
        },
      });
    });

    return cols;
  }, [handlePageTitleClick, handleUrlPathClick]);

  return (
    <Table
      data={tableData}
      columns={columns}
      isLoading={isLoading}
      loadingRows={3}
    />
  );
};
